'use client';
import { getYearLowestPriceTrackingProperties } from '@/domain/activity/black-friday-2026/utils/year-lowest-price';

import { fetchProductList } from '@/api/product';
import type { ListProduct } from '@/api/types/public';
import type { CompleteTrackingParams, ProductGroupInfo } from '@/app/(home-page)/types';
import Image from '@/components/Image';
import { useJump } from '@/components/MagicJump';
import ProductCard, { type Product } from '@/components/Product/ProductCard';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useStore } from '@/providers/app-store-provider';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { useHomeSpmModuleCode } from '@/app/(home-page)/utils/spm-module';
import logger from '@/utils/client-logger';
import { EProductImageType } from '@/utils/enums/goods';
import { setBlockFlags } from '@/utils/feature-value';
import { priceInfo } from '@/utils/price';
import { serializeKey } from '@/utils/swr/keys';
import { useCallback, useEffect, useMemo, useRef, type MouseEvent, type ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import useSWR from 'swr';

import HomeSectionHeading2026 from '../HomeSectionHeading2026/HomeSectionHeading2026';
import styles from './NewArrivals2026.module.css';
import type {
  NewArrivals2026Content,
  NewArrivals2026Props,
  NewArrivals2026ValidExposureParams,
  NewArrivalsListConfig,
} from './types';

const DEFAULT_PRODUCT_LIMIT = 12;
const SKELETON_ITEMS = ['new-arrivals-1', 'new-arrivals-2', 'new-arrivals-3', 'new-arrivals-4'];

type NewArrivalsProduct = ListProduct & {
  business_sku?: string | number;
  correlation?: Product['correlation'];
  fine_ranking_type?: string;
  sku?: string | number;
  in_stock?: string | number;
  recall_group_type?: string;
  recommend_result_type?: string;
};

function normalizeConfiguredText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/<br\s*\/?>/gi, '\n').trim();
}

function isProduct(value: unknown): value is NewArrivalsProduct {
  if (!value || typeof value !== 'object') return false;

  const product = value as { id?: unknown; shop_sku?: unknown };
  return Boolean(product.id) && typeof product.shop_sku === 'string' && Boolean(product.shop_sku);
}

function isAvailableProduct(product: NewArrivalsProduct): boolean {
  const status = Number(product.status);
  const stock = Number(product.stock);

  if (Number.isFinite(status) && status !== 1) return false;
  if (Number.isFinite(stock) && stock <= 0) return false;
  return String(product.in_stock) !== '2';
}

function getProductKey(product: ListProduct, index: number): string {
  return String(product.shop_sku || product.id || index);
}

function getProductGroup(config: NewArrivalsListConfig): ProductGroupInfo {
  return config.position_jump_product_group || config.product_group || {};
}

function getModuleTitle(content: NewArrivals2026Content): string {
  return normalizeConfiguredText(content.layout_title || content.display_prefix);
}

function NewArrivalsHeading({ content }: { content: NewArrivals2026Content }) {
  const title = normalizeConfiguredText(content.layout_title);
  const subtitle = normalizeConfiguredText(content.layout_subtitle);
  const shouldShowHeading = Number(content.layout_title_status) === 1 && Boolean(title || subtitle);

  if (!shouldShowHeading) return null;

  return (
    <HomeSectionHeading2026
      title={title}
      subtitle={subtitle}
      titleColor={content.layout_title_color || '#1A1C1E'}
      subtitleColor={content.layout_subtitle_color || content.layout_subtitl_color || undefined}
      titleTag="h2"
      subtitleTag="h5"
    />
  );
}

function shouldShowOperationImage(listConfig: NewArrivalsListConfig): boolean {
  return Number(listConfig.first_image_status) === 1 && Boolean(listConfig.jump_image);
}

function buildProductTrackingParams({
  product,
  productIndex,
  moduleIndex,
  indexData,
  content,
  listConfig,
  currency,
  userInfo,
  homeListData,
}: {
  product: NewArrivalsProduct;
  productIndex: number;
  moduleIndex: number;
  indexData: NewArrivals2026Props['indexData'];
  content: NewArrivals2026Content;
  listConfig: NewArrivalsListConfig;
  currency: { name?: string; exchange_rate?: string | number };
  userInfo: unknown;
  homeListData: Array<{ component_id?: string; id?: string | number }>;
}): CompleteTrackingParams {
  const currentPrice = priceInfo(userInfo, product, currency) as {
    price?: string;
    sell_price?: string;
  };
  const group = getProductGroup(listConfig);
  const regularListIndex = Math.max(
    homeListData
      .filter((item) => item.component_id === indexData.component_id)
      .findIndex((item) => String(item.id) === String(indexData.id)),
    0,
  );
  const blockFlags = `home@horizontal_item_${regularListIndex}@`;
  const blockFlagsExt = JSON.stringify({
    module_index: moduleIndex,
    module_name: indexData.component_id,
    module_title: normalizeConfiguredText(content.layout_title),
    module_tab_name: normalizeConfiguredText(listConfig.list_title),
  });

  return {
    module_index: moduleIndex,
    module_name: indexData.component_id,
    module_id: String(indexData.blocks_advances_id || indexData.id || ''),
    module_title: normalizeConfiguredText(content.layout_title),
    module_tab_name: normalizeConfiguredText(listConfig.list_title),
    mkt_index: productIndex,
    mkt_index_sub: '',
    mkt_jump_type: '2',
    mkt_jump_content: String(product.shop_sku || product.id || ''),
    shop_sku: String(product.shop_sku || ''),
    ...getYearLowestPriceTrackingProperties(product),
    business_sku: String(product.business_sku || ''),
    product_sku: String(product.sku || ''),
    product_id: String(product.id || ''),
    product_name: String(product.short_name || product.name || ''),
    group_id: String(group.id || listConfig.product_group_id || ''),
    group_name: group.name || '',
    product_price: Number(currentPrice.price || 0),
    current_product_price: Number(currentPrice.sell_price || 0),
    currency: currency.name || '',
    dollar_exchange_rate: Number(currency.exchange_rate || 1),
    fine_ranking_type: product.fine_ranking_type,
    recall_group_type: product.recall_group_type,
    recommend_result_type: product.recommend_result_type || '',
    block_flags: blockFlags,
    block_flags_ext: blockFlagsExt,
  };
}

function getFrameColorIndex(
  baseProduct: NewArrivalsProduct | undefined,
  selectedProduct: ListProduct,
): number | '' {
  if (
    Number(baseProduct?.is_glass) !== 1 ||
    !Array.isArray(baseProduct.correlation) ||
    baseProduct.correlation.length === 0
  ) {
    return '';
  }

  const selectedProductKey = getProductKey(selectedProduct, 0);
  const colorIndex = baseProduct.correlation.findIndex(
    (colorProduct, index) => getProductKey(colorProduct, index) === selectedProductKey,
  );

  return colorIndex >= 0 ? colorIndex : '';
}

function buildProductValidExposureParams({
  product,
  baseProduct,
  productIndex,
  moduleIndex,
  indexData,
  content,
}: {
  product: ListProduct;
  baseProduct: NewArrivalsProduct | undefined;
  productIndex: number;
  moduleIndex: number;
  indexData: NewArrivals2026Props['indexData'];
  content: NewArrivals2026Content;
}): NewArrivals2026ValidExposureParams {
  return {
    module_index: moduleIndex,
    module_id: String(indexData.blocks_advances_id || indexData.id || ''),
    module_title: getModuleTitle(content),
    module_name: indexData.component_id,
    module_tab_name: '',
    mkt_index: productIndex + 1,
    mkt_index_sub: getFrameColorIndex(baseProduct, product),
    mkt_jump_type: '2',
    mkt_jump_content: String(product.shop_sku || product.id || ''),
    ...getYearLowestPriceTrackingProperties(product),
  };
}

function buildOperationValidExposureParams({
  moduleIndex,
  indexData,
  content,
  listConfig,
}: {
  moduleIndex: number;
  indexData: NewArrivals2026Props['indexData'];
  content: NewArrivals2026Content;
  listConfig: NewArrivalsListConfig;
}): NewArrivals2026ValidExposureParams {
  return {
    module_index: moduleIndex,
    module_id: String(indexData.blocks_advances_id || indexData.id || ''),
    module_title: getModuleTitle(content),
    module_name: indexData.component_id,
    module_tab_name: '',
    mkt_index: 0,
    mkt_index_sub: '',
    mkt_jump_type: String(listConfig.jump_type ?? ''),
    mkt_jump_content: String(listConfig.Jump_target ?? ''),
  };
}

function ProductExposure({
  product,
  productIndex,
  spmCnt,
  onExposure,
  children,
}: {
  product: ListProduct;
  productIndex: number;
  /** 商品坑位完整编码（D 位，product_NNN）；模块码缺失（表外旧数据）时为空串。 */
  spmCnt?: string;
  onExposure: (product: ListProduct, productIndex: number) => void;
  children: ReactNode;
}) {
  const { ref: inViewRef, inView } = useInView({ threshold: 0, triggerOnce: true });
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与商品点击/有效曝光上报 spm_cnt 同值，
  // 商品卡内部 DOM 属共享 ProductCard，坑位码打在本挂载点容器上，表外静默不打标。
  const domSpm = useSpmDomAttr(spmCnt);

  useEffect(() => {
    if (inView) onExposure(product, productIndex);
  }, [inView, onExposure, product, productIndex]);

  return (
    <div ref={inViewRef} className={`relative ${styles.productItem}`} {...domSpm}>
      {children}
    </div>
  );
}

function NewArrivalsSkeleton({
  rootRef,
  content,
  showOperationImage,
}: {
  rootRef: (node?: Element | null) => void;
  content: NewArrivals2026Content;
  showOperationImage: boolean;
}) {
  return (
    <section ref={rootRef} className="w-full bg-white" aria-hidden>
      <NewArrivalsHeading content={content} />
      <div
        className={`grid px-[16px] min-[1280px]:px-[24px] ${
          showOperationImage
            ? `${styles.layoutWithOperationImage} grid-cols-2 gap-[16px] min-[1280px]:gap-[24px]`
            : 'h-[clamp(500px,40vw,768px)] grid-cols-1'
        }`}
      >
        {showOperationImage ? (
          <div className="h-full w-full animate-pulse bg-auxiliary-light-gray" />
        ) : null}
        <div
          className={`grid h-full gap-x-[16px] gap-y-[24px] overflow-hidden min-[1280px]:gap-x-[24px] min-[1280px]:gap-y-[32px] ${
            showOperationImage ? 'grid-cols-2' : 'grid-cols-4'
          }`}
        >
          {SKELETON_ITEMS.map((item) => (
            <div key={item} className="min-w-0 animate-pulse">
              <div className="aspect-square bg-auxiliary-light-gray" />
              <div className="mt-[12px] h-[17px] w-2/5 rounded bg-auxiliary-light-gray" />
              <div className="mt-[6px] h-[19px] w-1/3 rounded bg-auxiliary-light-gray" />
              <div className="mt-[12px] h-[22px] w-1/2 rounded bg-auxiliary-light-gray" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OperationImage({
  content,
  listConfig,
  spmCnt,
  onClick,
  onValidExposure,
}: {
  content: NewArrivals2026Content;
  listConfig: NewArrivalsListConfig;
  /** 运营图坑位完整编码（D 位，open_campaign）；模块码缺失（表外旧数据）时为空串。 */
  spmCnt?: string;
  onClick: () => void;
  onValidExposure: () => void;
}) {
  const group = getProductGroup(listConfig);
  const title = normalizeConfiguredText(listConfig.image_title);
  const description = normalizeConfiguredText(listConfig.description);
  const accessibleName =
    title ||
    getModuleTitle(content) ||
    normalizeConfiguredText(listConfig.list_title || group.name) ||
    description;
  const hasAction = Boolean(
    Number(listConfig.jump_type) > 0 && listConfig.Jump_target && accessibleName,
  );
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与点击/有效曝光上报 spm_cnt 同值，表外静默不打标。
  const domSpm = useSpmDomAttr(spmCnt);
  const { ref: validExposureRef } = useValidExposure({
    ts: `new-arrivals-operation-${String(listConfig.id || listConfig.jump_image)}`,
    callback: onValidExposure,
  });

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (event.defaultPrevented) return;
    onClick();
  };

  const imageContent = (
    <>
      <Image
        src={listConfig.jump_image!}
        alt={title}
        fill
        sizes="50vw"
        className="object-cover object-center"
      />
      {title || description ? (
        <span className="absolute inset-x-0 bottom-0 flex min-h-[90px] flex-col justify-end gap-[8px] bg-gradient-to-t from-black/50 to-transparent p-[16px] text-white">
          {title ? (
            <span
              className="font-bb text-[24px] leading-[28px]"
              style={{ color: listConfig.image_title_color || '#FFFFFF' }}
            >
              {title}
            </span>
          ) : null}
          {description ? (
            <span
              className="font-br text-[14px] leading-[22px]"
              style={
                listConfig.description_color ? { color: listConfig.description_color } : undefined
              }
              data-new-arrivals-operation-description
            >
              {description}
            </span>
          ) : null}
        </span>
      ) : null}
    </>
  );

  return (
    <div ref={validExposureRef} className="h-full w-full min-w-0" data-new-arrivals-scroll-proxy>
      {hasAction ? (
        <button
          type="button"
          className="relative block h-full w-full cursor-pointer overflow-hidden bg-[#f3f3f3] p-0 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple"
          aria-label={accessibleName}
          onClick={handleClick}
          {...domSpm}
        >
          {imageContent}
        </button>
      ) : (
        <div className="relative h-full w-full overflow-hidden bg-[#f3f3f3]">{imageContent}</div>
      )}
    </div>
  );
}

function NewArrivalsContent({
  indexData,
  content,
  listConfig,
  products,
  onWishChange,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
  homeListData = [],
}: NewArrivals2026Props & {
  content: NewArrivals2026Content;
  listConfig: NewArrivalsListConfig;
  products: NewArrivalsProduct[];
  onWishChange: () => void;
}) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const toJumpTarget = useJump();
  // 楼层 SPM 模块编码（C 位）：按楼块数据动态拼接，坑位码在此基础上追加。
  const spmModule = useHomeSpmModuleCode(indexData.component_id, indexData.id, 'home_pc');
  // 商品坑位 D 位编码（动态商品位，序号 3 位：product_001）；模块码缺失（表外旧数据）时为空串，不硬造。
  const buildProductSpmCnt = useCallback(
    (productIndex: number) =>
      spmModule ? `${spmModule}.product_${String(productIndex + 1).padStart(3, '0')}` : '',
    [spmModule],
  );
  // 运营图坑位 D 位编码（open_campaign）：点击与有效曝光共用。
  const openCampaignSpmCnt = spmModule ? `${spmModule}.open_campaign` : '';
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与上报编码同值，表外静默不打标。
  const moduleDomSpm = useSpmDomAttr(spmModule);
  const openCampaignDomSpm = useSpmDomAttr(openCampaignSpmCnt);
  const exposedProductKeys = useRef(new Set<string>());
  const validExposedProductKeys = useRef(new Set<string>());
  const operationValidExposed = useRef(false);
  const showOperationImage = shouldShowOperationImage(listConfig);
  const nameSuffix = normalizeConfiguredText(content.name_suffix);

  const buildTrackingParams = useCallback(
    (product: NewArrivalsProduct, productIndex: number) =>
      buildProductTrackingParams({
        product,
        productIndex,
        moduleIndex,
        indexData,
        content,
        listConfig,
        currency,
        userInfo,
        homeListData,
      }),
    [content, currency, homeListData, indexData, listConfig, moduleIndex, userInfo],
  );

  const handleProductClick = useCallback(
    (productIndex: number, product: ListProduct) => {
      const trackingParams = buildTrackingParams(product as NewArrivalsProduct, productIndex);
      const clickParams = buildProductValidExposureParams({
        product,
        baseProduct: products[productIndex],
        productIndex,
        moduleIndex,
        indexData,
        content,
      });

      try {
        setBlockFlags(trackingParams);
        // 商品点击上报坑位级 spm_cnt（动态商品位，序号 3 位：product_001）。
        onTrack?.(withSpmCnt(clickParams, buildProductSpmCnt(productIndex)));
      } catch (error) {
        logger.error('2026 PC New Arrivals product click tracking error:', error);
      }
    },
    [buildProductSpmCnt, buildTrackingParams, content, indexData, moduleIndex, onTrack, products],
  );

  const handleProductExposure = useCallback(
    (product: ListProduct, productIndex: number) => {
      if (!onExpTrack) return;
      const productKey = getProductKey(product, productIndex);
      if (exposedProductKeys.current.has(productKey)) return;

      try {
        onExpTrack(buildTrackingParams(product as NewArrivalsProduct, productIndex));
        exposedProductKeys.current.add(productKey);
      } catch (error) {
        logger.error('2026 PC New Arrivals product exposure tracking error:', error);
      }
    },
    [buildTrackingParams, onExpTrack],
  );

  const handleProductValidExposure = useCallback(
    (productIndex: number, product: ListProduct) => {
      if (!onValidExpTrack) return;
      const baseProduct = products[productIndex];
      const colorIndex = getFrameColorIndex(baseProduct, product);
      const productKey = `${productIndex}-${getProductKey(product, productIndex)}-${colorIndex}`;
      if (validExposedProductKeys.current.has(productKey)) return;

      try {
        onValidExpTrack(
          // 商品有效曝光为 item 级，spm_cnt 追加坑位 D 位（动态商品位，序号 3 位：product_001）。
          withSpmCnt(
            buildProductValidExposureParams({
              product,
              baseProduct,
              productIndex,
              moduleIndex,
              indexData,
              content,
            }),
            buildProductSpmCnt(productIndex),
          ),
        );
        validExposedProductKeys.current.add(productKey);
      } catch (error) {
        logger.error('2026 PC New Arrivals product valid exposure tracking error:', error);
      }
    },
    [buildProductSpmCnt, content, indexData, moduleIndex, onValidExpTrack, products],
  );

  const operationValidExposureParams = useMemo(
    () => buildOperationValidExposureParams({ moduleIndex, indexData, content, listConfig }),
    [content, indexData, listConfig, moduleIndex],
  );

  const handleOperationClick = useCallback(() => {
    try {
      // 运营图点击即打开专题活动，上报坑位级 spm_cnt；浅拷贝避免污染曝光共用对象。
      onTrack?.(withSpmCnt(operationValidExposureParams, openCampaignSpmCnt));
    } catch (error) {
      logger.error('2026 PC New Arrivals operation image click tracking error:', error);
    }
    toJumpTarget(listConfig);
  }, [listConfig, onTrack, openCampaignSpmCnt, operationValidExposureParams, toJumpTarget]);

  const handleOperationValidExposure = useCallback(() => {
    if (!onValidExpTrack || operationValidExposed.current) return;

    try {
      // 运营图有效曝光为 item 级，spm_cnt 追加坑位 D 位（open_campaign，与点击同坑位）。
      onValidExpTrack(withSpmCnt(operationValidExposureParams, openCampaignSpmCnt));
      operationValidExposed.current = true;
    } catch (error) {
      logger.error('2026 PC New Arrivals operation image exposure tracking error:', error);
    }
  }, [onValidExpTrack, openCampaignSpmCnt, operationValidExposureParams]);

  return (
    <section
      className="w-full bg-white text-primary-text"
      style={
        Number(content.bg_color_status) === 1
          ? { backgroundColor: content.bg_color || '#FFFFFF' }
          : undefined
      }
      data-component-id={indexData.component_id}
      {...moduleDomSpm}
    >
      <NewArrivalsHeading content={content} />

      <div
        className={`grid px-[16px] min-[1280px]:px-[24px] ${
          showOperationImage
            ? `${styles.layoutWithOperationImage} grid-cols-2 gap-[16px] min-[1280px]:gap-[24px]`
            : 'h-[clamp(500px,40vw,768px)] grid-cols-1'
        }`}
      >
        {showOperationImage ? (
          <OperationImage
            content={content}
            listConfig={listConfig}
            spmCnt={openCampaignSpmCnt}
            onClick={handleOperationClick}
            onValidExposure={handleOperationValidExposure}
          />
        ) : null}

        <div
          className={`h-full min-h-0 min-w-0 overflow-y-auto ${styles.productScroller}`}
          data-new-arrivals-product-scroll
        >
          {/* 商品卡收藏坑位码由 spmModule 动态拼接，无需 SPM 作用域查表。 */}
          <div
            className={`grid gap-x-[16px] gap-y-[24px] pr-[1px] min-[1280px]:gap-x-[24px] min-[1280px]:gap-y-[32px] ${
              showOperationImage ? 'grid-cols-2' : 'grid-cols-4'
            }`}
          >
            {products.map((product, productIndex) => (
              <ProductExposure
                key={`${getProductKey(product, productIndex)}-${JSON.stringify(product)}`}
                product={product}
                productIndex={productIndex}
                spmCnt={buildProductSpmCnt(productIndex)}
                onExposure={handleProductExposure}
              >
                <ProductCard
                  product={product}
                  index={productIndex}
                  spmModule={spmModule}
                  wished={onWishChange}
                  removed={onWishChange}
                  className="min-w-0 overflow-hidden bg-primary-white"
                  imageClassName="!aspect-square !min-h-0 !rounded-none"
                  productInfoClassName="!px-[8px] !pb-[16px] !pt-[12px] [&_.row-start-2]:min-h-[24px]"
                  imageHoverRule={[EProductImageType.PRODUCT]}
                  showTryOn={false}
                  showMember={false}
                  showTag={false}
                  showName={false}
                  namePriceInline={false}
                  nameSuffix={nameSuffix}
                  showCorrelation
                  showReviewStar
                  showCorrelationCount={6}
                  collectScene="2026 PC 首页 New Arrivals"
                  loginSituation="2026 PC 首页 New Arrivals 收藏"
                  handelClick={handleProductClick}
                  onValidExposure={handleProductValidExposure}
                />
                <a
                  href={`/goods-detail/${product.shop_sku}`}
                  aria-label={normalizeConfiguredText(
                    product.short_name || product.name || product.shop_sku,
                  )}
                  className="pointer-events-none absolute inset-0 z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple"
                  onClick={() => handleProductClick(productIndex, product)}
                >
                  <span className="sr-only">
                    {normalizeConfiguredText(
                      product.short_name || product.name || product.shop_sku,
                    )}
                  </span>
                </a>
              </ProductExposure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function NewArrivals2026(props: NewArrivals2026Props) {
  const { indexData } = props;
  const currency = useStore((state) => state.currency);
  const token = useStore((state) => state.token);
  const content = indexData.content ?? indexData;
  const listConfig = Array.isArray(content.product_list)
    ? content.product_list.find(
        (item) => Number(item.product_group_id || getProductGroup(item).id) > 0,
      )
    : undefined;
  const groupId = Number(
    listConfig?.product_group_id || (listConfig && getProductGroup(listConfig).id),
  );
  const productLimit = Math.max(Number(listConfig?.list_product_num) || DEFAULT_PRODUCT_LIMIT, 1);
  const staticProducts = Array.isArray(indexData.products)
    ? indexData.products.filter(isProduct).filter(isAvailableProduct).slice(0, productLimit)
    : [];
  const { ref: inViewRef, inView } = useInView({ threshold: 0, triggerOnce: true });
  const requestKey =
    listConfig && groupId > 0 && staticProducts.length === 0 && inView
      ? serializeKey([
          'newArrivals2026',
          indexData.id,
          listConfig.id,
          groupId,
          productLimit,
          currency?.name,
          Boolean(token),
        ])
      : null;
  const {
    data: productData,
    error: productError,
    isLoading,
    mutate: refreshProducts,
  } = useSWR<NewArrivalsProduct[] | null>(
    requestKey,
    async () => {
      const response = await fetchProductList({
        group_id: groupId,
        page: 1,
        limit: productLimit,
        currency: currency?.name,
        append_correlation: 1,
        in_stock: 1,
      });
      const responseProducts: unknown = response.data?.list;

      if (response.code !== 200 || !Array.isArray(responseProducts)) {
        throw new Error('regularListNewArrivals 商品接口响应异常');
      }

      return responseProducts.filter(isProduct).filter(isAvailableProduct).slice(0, productLimit);
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: 300000,
      keepPreviousData: true,
    },
  );
  const products = staticProducts.length > 0 ? staticProducts : productData || [];
  const hasAttemptedFetch =
    staticProducts.length > 0 || productData !== undefined || Boolean(productError);

  if (!listConfig || groupId <= 0 || Number(content.product_card_style) === 1) return null;
  if (hasAttemptedFetch && products.length === 0) return null;
  if (!hasAttemptedFetch || (isLoading && products.length === 0)) {
    return (
      <NewArrivalsSkeleton
        rootRef={inViewRef}
        content={content}
        showOperationImage={Boolean(listConfig && shouldShowOperationImage(listConfig))}
      />
    );
  }

  return (
    <NewArrivalsContent
      {...props}
      content={content}
      listConfig={listConfig}
      products={products}
      onWishChange={() => void refreshProducts()}
    />
  );
}
